import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { GoogleMap, Circle, useLoadScript } from "@react-google-maps/api";
import { ExternalLink, MapPin } from "lucide-react";
import { getCoordinatesFromZipCode } from "~/lib/helpers/storehelpers";
import { useSupabaseContext } from "~/lib/supabase_context";
import { usZip5Base } from "~/lib/usZip";

interface ZipCodeMapProps {
  zipCode: string;
  /** Full street address for Google Geocoder — pin at storefront, not only ZIP centroid. */
  markerAddress?: string | null;
  label?: string;
  height?: string;
  className?: string;
  zoom?: number;
  deliveryRadius?: number | null;
}

type LatLng = {
  lat: number;
  lng: number;
};

function useZipCenter(zipCode: string) {
  const [center, setCenter] = useState<LatLng | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!zipCode.trim() || !usZip5Base(zipCode)) {
        setCenter(null);
        setError("No valid US ZIP for this location.");
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        setError(null);
        const coordinates = await getCoordinatesFromZipCode(zipCode);
        if (coordinates) {
          setCenter(coordinates);
        } else {
          setCenter(null);
          setError("Unable to find coordinates for this ZIP code.");
        }
      } catch {
        setCenter(null);
        setError("Unable to load map location.");
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [zipCode]);

  return { center, isLoading, error };
}

function OsmZipMap({
  center,
  zipCode,
  height,
  className,
}: {
  center: LatLng;
  zipCode: string;
  height: string;
  className: string;
}) {
  const delta = 0.04;
  const bbox = useMemo(
    () =>
      `${center.lng - delta},${center.lat - delta},${center.lng + delta},${center.lat + delta}`,
    [center.lat, center.lng],
  );
  const embedSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${center.lat}%2C${center.lng}`;
  const externalHref = `https://www.google.com/maps/search/?api=1&query=${center.lat},${center.lng}`;

  return (
    <div className={`w-full space-y-2 ${className}`}>
      <div
        className="w-full overflow-hidden rounded-lg border border-gray-300 shadow-md"
        style={{ height }}
      >
        <iframe
          title={`Map near ZIP ${zipCode}`}
          className="h-full w-full border-0"
          src={embedSrc}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
      <a
        href={externalHref}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
      >
        <ExternalLink className="h-4 w-4" />
        Open in Google Maps
      </a>
    </div>
  );
}

function GoogleZipMap({
  center,
  zipCode,
  markerAddress,
  label,
  height,
  className,
  zoom,
  deliveryRadius,
  googleApiKey,
}: {
  center: LatLng;
  zipCode: string;
  markerAddress?: string | null;
  label?: string;
  height: string;
  className: string;
  zoom: number;
  deliveryRadius?: number | null;
  googleApiKey: string;
}) {
  const { isLoaded, loadError } = useLoadScript({
    id: "google-map-script-store-zip",
    googleMapsApiKey: googleApiKey,
  });

  const radiusInMeters = deliveryRadius ? deliveryRadius * 1609.34 : null;
  const adjustedZoom = deliveryRadius && radiusInMeters
    ? Math.min(zoom, Math.max(10, 15 - Math.log10(radiusInMeters / 1000)))
    : zoom;

  const [mapCenter, setMapCenter] = useState<LatLng>(center);
  const [pin, setPin] = useState<LatLng>(center);
  const [mapZoom, setMapZoom] = useState(adjustedZoom);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);

  const markerTitle = useMemo(
    () =>
      label ||
      (markerAddress?.trim()
        ? markerAddress.trim()
        : `ZIP ${usZip5Base(zipCode) || zipCode}`),
    [label, markerAddress, zipCode],
  );

  useEffect(() => {
    setMapCenter(center);
    setPin(center);
    setMapZoom(adjustedZoom);
  }, [center.lat, center.lng, adjustedZoom]);

  useEffect(() => {
    if (!isLoaded || typeof google === "undefined" || !google.maps?.Geocoder) {
      return;
    }
    const addr = markerAddress?.trim();
    if (!addr) {
      setPin(center);
      setMapCenter(center);
      setMapZoom(adjustedZoom);
      return;
    }
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: addr }, (results, status) => {
      if (status === "OK" && results?.[0]?.geometry?.location) {
        const loc = results[0].geometry.location;
        const p = { lat: loc.lat(), lng: loc.lng() };
        setPin(p);
        setMapCenter(p);
        setMapZoom(Math.max(adjustedZoom, 15));
      } else {
        setPin(center);
        setMapCenter(center);
        setMapZoom(adjustedZoom);
      }
    });
  }, [isLoaded, markerAddress, center.lat, center.lng, adjustedZoom]);

  /** Declarative <Marker /> can fail to bind in some React timing cases; use Maps JS API directly. */
  useEffect(() => {
    if (!map || typeof google === "undefined") return;

    markerRef.current?.setMap(null);
    markerRef.current = new google.maps.Marker({
      map,
      position: pin,
      title: markerTitle,
      zIndex: 10,
      animation: google.maps.Animation.DROP,
    });

    return () => {
      markerRef.current?.setMap(null);
      markerRef.current = null;
    };
  }, [map, pin.lat, pin.lng, markerTitle]);

  const onMapLoad = useCallback((m: google.maps.Map) => {
    setMap(m);
  }, []);

  const onMapUnmount = useCallback((_instance: google.maps.Map) => {
    markerRef.current?.setMap(null);
    markerRef.current = null;
    setMap(null);
  }, []);

  if (loadError) {
    return (
      <div
        className={`flex w-full items-center justify-center rounded-lg bg-gray-200 ${className}`}
        style={{ height }}
      >
        <div className="px-4 text-center">
          <MapPin className="mx-auto mb-2 h-8 w-8 text-gray-400" />
          <p className="text-sm text-gray-600">Google Maps could not load.</p>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${center.lat},${center.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
          >
            <ExternalLink className="h-4 w-4" />
            Open in Google Maps
          </a>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        className={`flex w-full items-center justify-center rounded-lg bg-gray-200 ${className}`}
        style={{ height }}
      >
        <p className="text-gray-600">Loading map…</p>
      </div>
    );
  }

  return (
    <div
      className={`w-full overflow-hidden rounded-lg border border-gray-300 shadow-md ${className}`}
      style={{ height }}
    >
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        zoom={mapZoom}
        center={mapCenter}
        onLoad={onMapLoad}
        onUnmount={onMapUnmount}
        options={{
          zoomControl: true,
          streetViewControl: true,
          mapTypeControl: false,
          fullscreenControl: true,
        }}
      >
        {radiusInMeters && (
          <Circle
            center={pin}
            radius={radiusInMeters}
            options={{
              fillColor: "#3b82f6",
              fillOpacity: 0.2,
              strokeColor: "#2563eb",
              strokeOpacity: 0.8,
              strokeWeight: 2,
            }}
          />
        )}
      </GoogleMap>
    </div>
  );
}

export function ZipCodeMap({
  zipCode,
  markerAddress,
  label,
  height = "400px",
  className = "",
  zoom = 12,
  deliveryRadius,
}: ZipCodeMapProps) {
  const { googleApiKey } = useSupabaseContext();
  const trimmedKey = googleApiKey?.trim() ?? "";
  const { center, isLoading, error } = useZipCenter(zipCode);

  if (isLoading) {
    return (
      <div
        className={`flex w-full items-center justify-center rounded-lg bg-gray-200 ${className}`}
        style={{ height }}
      >
        <p className="text-gray-600">Loading location…</p>
      </div>
    );
  }

  if (error || !center) {
    return (
      <div
        className={`flex w-full items-center justify-center rounded-lg bg-gray-200 ${className}`}
        style={{ height }}
      >
        <div className="max-w-sm px-4 text-center">
          <MapPin className="mx-auto mb-2 h-8 w-8 text-gray-400" />
          <p className="text-sm text-gray-600">{error ?? "Location not available."}</p>
          <p className="mt-1 text-xs text-gray-500">ZIP: {zipCode}</p>
        </div>
      </div>
    );
  }

  if (trimmedKey) {
    return (
      <GoogleZipMap
        center={center}
        zipCode={zipCode}
        markerAddress={markerAddress}
        label={label}
        height={height}
        className={className}
        zoom={zoom}
        deliveryRadius={deliveryRadius}
        googleApiKey={trimmedKey}
      />
    );
  }

  return (
    <OsmZipMap
      center={center}
      zipCode={zipCode}
      height={height}
      className={className}
    />
  );
}
